/**
 * SellerListPage — admin sellers management (STORY-008-007). Cloned from the
 * shipped `QueueListPage`: search box + `verification_status` filter chips, a
 * responsive `AdminTable`, "Load more" cursor pagination, and a row-activated
 * `SellerDrawer` (detail / edit / suspend / restore / notify).
 *
 * NOTE (drift, M1 Task 6): the admin seller LIST endpoint has NO free-text `q`
 * param (AC#2 "FTS within 500ms" is unmet server-side). MVP: a client-side
 * substring filter on the loaded page (business_name / pavilion_number) plus the
 * server-side `verification_status` chips. A server-side seller search is a
 * backend follow-up on STORY-003-001 if launch needs it.
 */
import {
  useEffect,
  useMemo,
  useState,
  type ReactElement,
} from "react";
import { useTranslation } from "react-i18next";

import { Button, GPill } from "@garden/ui";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { AdminTable } from "@/features/admin/components/AdminTable";
import type { AdminTableSort } from "@/features/admin/components/AdminTable";
import { verificationTone } from "@/features/admin/components/tone";
import {
  SELLER_VERIFICATION_STATUSES,
  type AdminSeller,
} from "@/features/admin/sellers/api";
import { useSellers, type SellerFilters } from "@/features/admin/sellers/hooks";
import { SellerCreateDialog } from "@/features/admin/sellers/SellerCreateDialog";
import { SellerDrawer } from "@/features/admin/sellers/SellerDrawer";

type SellerSortKey = "name" | "market" | "pavilion" | "phone" | "status";

/** Column -> comparable string, for the client-side sort (SCRUM-154). No
 * backend `order_by`/`sort` param exists on `GET /admin/marketplace/sellers`
 * (repository.list_sellers has none), so this sorts the currently-loaded
 * page(s) of rows only, per the simplest-safe option. */
function sortValue(s: AdminSeller, key: SellerSortKey): string {
  switch (key) {
    case "name":
      return s.business_name;
    case "market":
      return s.market_id;
    case "pavilion":
      return s.pavilion_number;
    case "phone":
      return s.phone_e164;
    case "status":
      return s.verification_status;
    default:
      return "";
  }
}

function useDebounced<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

export function SellerListPage(): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);

  const [verificationStatus, setVerificationStatus] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounced(search, 300).trim().toLowerCase();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [sortKey, setSortKey] = useState<SellerSortKey | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const filters: SellerFilters = { verificationStatus };
  const { data, isLoading, isError, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useSellers(filters);

  const allSellers: AdminSeller[] = useMemo(
    () => data?.pages.flatMap((p) => p.sellers) ?? [],
    [data],
  );

  const filteredRows = useMemo(() => {
    if (debouncedSearch === "") return allSellers;
    return allSellers.filter(
      (s) =>
        s.business_name.toLowerCase().includes(debouncedSearch) ||
        s.pavilion_number.toLowerCase().includes(debouncedSearch),
    );
  }, [allSellers, debouncedSearch]);

  const rows = useMemo(() => {
    if (sortKey === null) return filteredRows;
    const dir = sortDir === "asc" ? 1 : -1;
    return [...filteredRows].sort(
      (a, b) =>
        sortValue(a, sortKey).localeCompare(sortValue(b, sortKey), undefined, {
          numeric: true,
          sensitivity: "base",
        }) * dir,
    );
  }, [filteredRows, sortKey, sortDir]);

  const handleSort = (key: string): void => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key as SellerSortKey);
      setSortDir("asc");
    }
  };

  const tableSort: AdminTableSort = {
    key: sortKey,
    direction: sortDir,
    onSort: handleSort,
    ariaLabel: (column, direction) =>
      direction === "asc"
        ? t("common.sort.ascending", { column: column.label })
        : direction === "desc"
          ? t("common.sort.descending", { column: column.label })
          : t("common.sort.unsorted", { column: column.label }),
  };

  const columns = [
    { key: "name", label: t("sellers.columns.name"), sortable: true },
    { key: "market", label: t("sellers.columns.market"), sortable: true },
    { key: "pavilion", label: t("sellers.columns.pavilion"), sortable: true },
    { key: "phone", label: t("sellers.columns.phone"), sortable: true },
    { key: "status", label: t("sellers.columns.status"), sortable: true },
  ] as const;

  const renderCell = (s: AdminSeller, key: string): ReactElement | string => {
    switch (key) {
      case "name":
        return <span className="font-medium">{s.business_name}</span>;
      case "market":
        return (
          <span className="text-[var(--g-secondary)]">{s.market_id.slice(0, 8)}</span>
        );
      case "pavilion":
        return s.pavilion_number;
      case "phone":
        return <span className="text-[var(--g-secondary)]">{s.phone_e164}</span>;
      case "status":
        return (
          <span className="flex flex-wrap gap-1">
            <GPill tone={verificationTone(s.verification_status)} size="sm">
              {t(`sellers.verification.${s.verification_status}`, {
                defaultValue: s.verification_status,
              })}
            </GPill>
            {s.vacation_mode ? (
              <GPill tone="warn" size="sm">
                {t("sellers.vacationOn")}
              </GPill>
            ) : null}
          </span>
        );
      default:
        return "";
    }
  };

  const renderMobileCard = (s: AdminSeller): ReactElement => (
    <div className="flex items-start justify-between gap-2">
      <div>
        <div className="font-medium">{s.business_name}</div>
        <div className="mt-0.5 text-sm text-[var(--g-secondary)]">
          {s.market_id.slice(0, 8)} · {s.pavilion_number}
        </div>
        <div className="mt-1 text-xs text-[var(--g-secondary)]">{s.phone_e164}</div>
      </div>
      <div className="flex flex-col items-end gap-1">
        <GPill tone={verificationTone(s.verification_status)} size="sm">
          {t(`sellers.verification.${s.verification_status}`, {
            defaultValue: s.verification_status,
          })}
        </GPill>
        {s.vacation_mode ? (
          <GPill tone="warn" size="sm">
            {t("sellers.vacationOn")}
          </GPill>
        ) : null}
      </div>
    </div>
  );

  return (
    <div data-testid="admin-sellers-page" className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-xl font-bold">{t("sellers.title")}</h1>
        <Button
          variant="primary"
          size="md"
          data-testid="admin-sellers-new"
          onClick={() => setCreateOpen(true)}
          className="min-h-[44px]"
        >
          {t("sellers.action.create")}
        </Button>
      </div>

      {/* Toolbar: search + verification chips */}
      <div className="flex flex-col gap-3">
        <input
          type="search"
          data-testid="admin-sellers-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("sellers.searchPlaceholder")}
          aria-label={t("common.search")}
          className="min-h-[44px] w-full max-w-md rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-surface)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--g-primary)]"
        />
        <div className="flex flex-wrap items-center gap-2" data-testid="admin-sellers-filters">
          <button
            type="button"
            onClick={() => setVerificationStatus(null)}
            aria-pressed={verificationStatus === null}
            className={[
              "min-h-[44px] rounded-full px-3 py-1 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]",
              verificationStatus === null
                ? "bg-[var(--g-primary)] text-white"
                : "bg-[var(--g-elevated)] text-[var(--g-secondary)] hover:bg-[var(--g-hairline)]",
            ].join(" ")}
          >
            {t("sellers.verification.all")}
          </button>
          {SELLER_VERIFICATION_STATUSES.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setVerificationStatus(s)}
              aria-pressed={verificationStatus === s}
              data-testid={`admin-sellers-filter-${s}`}
              className={[
                "min-h-[44px] rounded-full px-3 py-1 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]",
                verificationStatus === s
                  ? "bg-[var(--g-primary)] text-white"
                  : "bg-[var(--g-elevated)] text-[var(--g-secondary)] hover:bg-[var(--g-hairline)]",
              ].join(" ")}
            >
              {t(`sellers.verification.${s}`)}
            </button>
          ))}
        </div>
      </div>

      <AdminTable<AdminSeller>
        columns={columns}
        rows={rows}
        rowKey={(s) => s.id}
        renderCell={renderCell}
        renderMobileCard={renderMobileCard}
        onRowActivate={(s) => setSelectedId(s.id)}
        rowAriaLabel={(s) => s.business_name}
        testid="admin-sellers"
        isLoading={isLoading}
        isError={isError}
        emptyLabel={t("sellers.empty")}
        errorLabel={t("error.loadFailed")}
        sort={tableSort}
      />

      {hasNextPage ? (
        <button
          type="button"
          onClick={() => void fetchNextPage()}
          disabled={isFetchingNextPage}
          className="mx-auto min-h-[44px] rounded-full bg-[var(--g-elevated)] px-6 py-2 text-sm font-medium text-[var(--g-secondary)] hover:bg-[var(--g-hairline)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)] disabled:opacity-50"
        >
          {isFetchingNextPage ? t("common.refreshing") : t("common.loadMore")}
        </button>
      ) : null}

      <SellerDrawer sellerId={selectedId} onClose={() => setSelectedId(null)} />

      <SellerCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSuccess={() => setCreateOpen(false)}
      />
    </div>
  );
}

export default SellerListPage;
